/**
 * processors/healthProcessor.js
 * 
 * Procesador de datos de salud para monitoreo de oxigenación
 * 
 * CONTEXTO CLÍNICO:
 * - loval (lower value): Umbral inferior de oxigenación segura
 * - upval (upper value): Umbral superior de oxigenación segura
 * - measu < loval: Hipoxemia crítica, requiere inyección de oxígeno
 * - measu entre loval y upval: Rango seguro
 * - measu > upval: Hiperóxia, posible toxicidad por oxígeno
 * 
 * ESCALA DE ESTADOS DEVUELTOS:
 * 1 = CRÍTICO (muy baja oxigenación, inyectar O2 urgente)
 * 2 = MALO (baja oxigenación, inyectar O2)
 * 3 = TOLERABLE (ligeramente baja, monitorear)
 * 4 = ACEPTABLE (dentro de rango seguro)
 * 5 = BUENO (dentro de rango, margen seguro)
 * 6 = EXCELENTE (óptimo, vigilar exceso)
 */

/**
 * Calcula el estado de oxigenación basado en un array de parámetros monitoreados
 * 
 * @param {Array} hpia - Health Processor Input Array
 *   Estructura: [{ idPar, cntrl, upval, measu, loval }, ...]
 * @returns {number|null} hpov - Health Processor Output Value (1-6 o null)
 */
export function healthData2Status(hpia) {
  // Validar entrada
  if (!Array.isArray(hpia) || hpia.length === 0) {
    return null;
  }

  // Calcular hpov(ix) para cada elemento
  const hpovArray = hpia.map((element) => 
    calculateIndividualHealth(element)
  );

  // Calcular hpov final: mínimo de valores no nulos (más restrictivo)
  const nonNullValues = hpovArray.filter(v => v !== null);
  
  if (nonNullValues.length === 0) {
    return null;
  }

  return Math.min(...nonNullValues);
}

/**
 * Calcula el estado de oxigenación individual para un parámetro
 * 
 * @param {Object} element - { idPar, cntrl, upval, measu, loval }
 * @param {number} ix - Índice del elemento en el array
 * @returns {number|null} hpov(ix)
 */
function calculateIndividualHealth(element) {
  const { idPar, cntrl, upval, measu, loval } = element;

  // Validación 0: idPar no debe ser null
  if (idPar === null) {
    return null;
  }

    // Validación 1: cntrl debe ser true
  if (cntrl !== true) {
    return null;
  }

  // Validación 2: measu no debe ser null
  if (measu === null || measu === undefined) {
    return null;
  }

  // Validación 3: al menos uno de upval o loval debe ser no-null
  if (upval === null && loval === null) {
    return null;
  }

  // Validación 4: upval no debe ser menor que loval (si ambos existen)
  if (upval !== null && loval !== null && upval < loval) {
    return null;
  }

  // CASO ESPECIAL: uno y solo uno es null, o ambos son iguales
  if (isSpecialCase(upval, loval)) {
    const value = upval !== null ? upval : loval;
    // Si measu > value (threshold): estado óptimo (6)
    // Si measu <= value: estado bajo (1)
    return measu > value ? 6 : 1;
  }

  // CASO NORMAL: ambos upval y loval existen y upval > loval
  if (upval !== null && loval !== null && upval > loval) {
    const semidiff = (upval - loval) / 2;
    return evaluateNormalCase(measu, upval, loval, semidiff);
  }

  // Si uno es null pero no es caso especial
  const value = upval !== null ? upval : loval;
  return measu > value ? 6 : 1;
}

/**
 * Determina si es un caso especial (uno null o ambos iguales)
 * 
 * @param {number|null} upval 
 * @param {number|null} loval 
 * @returns {boolean}
 */
function isSpecialCase(upval, loval) {
  // Uno y solo uno es null
  const oneIsNull = (upval === null && loval !== null) || 
                    (upval !== null && loval === null);
  
  // Ambos son iguales (y no nulos)
  const bothEqual = upval !== null && loval !== null && upval === loval;

  return oneIsNull || bothEqual;
}

/**
 * Evalúa el estado en el caso normal (ambos límites válidos y diferentes)
 * 
 * @param {number} measu - Valor medido
 * @param {number} upval - Umbral superior (límite seguro máximo)
 * @param {number} loval - Umbral inferior (límite seguro mínimo)
 * @param {number} semidiff - Semi-diferencia (upval - loval) / 2
 * @returns {number} Estado de salud (1-6)
 */
function evaluateNormalCase(measu, upval, loval, semidiff) {
  // Por encima del rango seguro
  if (measu > upval + semidiff) {
    return 6; // EXCELENTE (posible exceso de oxígeno)
  }
  if (measu > upval && measu <= upval + semidiff) {
    return 5; // BUENO (margen seguro superior)
  }

  // Dentro del rango seguro
  if (measu > loval + semidiff && measu <= upval) {
    return 4; // ACEPTABLE (centro del rango)
  }
  if (measu > loval && measu <= loval + semidiff) {
    return 3; // TOLERABLE (margen bajo del rango)
  }

  // Por debajo del rango seguro - REQUIERE OXÍGENO
  if (measu > loval - semidiff && measu <= loval) {
    return 2; // MALO (ligeramente bajo, inyectar O2)
  }
  // measu <= loval - semidiff
  return 1; // CRÍTICO (muy bajo, inyectar O2 URGENTE)
}

/**
 * Función de debug para mostrar información detallada en consola
 * Contextualizada para monitoreo de oxigenación
 * 
 * @param {Array} hpia - Health Processor Input Array
 * @param {number|null} hpov - Resultado de healthData2Status
 */
export function healthProcessorDebug(hpia, hpov = null) {
  const timestamp = new Date().toLocaleString('es-ES');
  
  console.group(`%c[HEALTH PROCESSOR DEBUG - OXIGENACIÓN] ${timestamp}`, 
    'color: #0066cc; font-weight: bold; font-size: 14px');

  // Resumen general
  console.log('%cRESUMEN GENERAL:', 'color: #333; font-weight: bold');
  console.log(`Total elementos monitoreados: ${hpia.length}`);
  console.log(`Valor final hpov: ${hpov !== null ? hpov : 'null'}`);
  console.log(`Estado general: ${getStatusLabel(hpov)}`);

  // Alerta si estado es crítico
  if (hpov === 1 || hpov === 2) {
    console.warn(
      `%c⚠️ ACCIÓN REQUERIDA: Oxigenación baja. Preparar inyección de oxígeno.`,
      'color: #ff0000; font-weight: bold; font-size: 12px'
    );
  }

  // Detalles por elemento
  console.log('%cDETALLE POR ELEMENTO:', 'color: #333; font-weight: bold');
  
  const debugDetails = hpia.map((element, ix) => {
    const { idPar, cntrl, upval, measu, loval } = element;
    const hpovIx = calculateIndividualHealth(element);
    const validations = performValidations(element);
  //  const rangeInfo = getRangeInfo(measu, upval, loval); // lost

    return {
      ix,
      'ID': idPar || '—',
      'Cntrl': cntrl,
      'Estado': getStatusLabelShort(hpovIx),
//      'O2 Medido': measu !== null ? measu.toFixed(2) : '—',
      'O2 Medido': measu,
      'Rango Seguro': `[${loval?.toFixed(2) || '—'}, ${upval?.toFixed(2) || '—'}]`,
      // 'Posición': rangeInfo,
      'Acción': getActionRequired(hpovIx),
      '✓': Object.values(validations).every(v => v === '✓') ? 'VALIDO' : '⚠️ INVÁLIDO'
    };
  });

  console.table(debugDetails);

  // Análisis detallado de validaciones
  console.log('%cDETALLE DE VALIDACIONES:', 'color: #333; font-weight: bold');
  const validationDetails = hpia.map((element, ix) => ({
    'ID': element.idPar || ix,
    'cntrl=true': element.cntrl === true ? '✓' : '✗',
    'measu≠null': element.measu !== null ? '✓' : '✗',
    'límites≠null': (element.upval !== null || element.loval !== null) ? '✓' : '✗',
    'upval≥loval': (element.upval === null || element.loval === null || element.upval >= element.loval) ? '✓' : '✗'
  }));
  console.table(validationDetails);

  // Análisis de distribución
  console.log('%cDISTRIBUCIÓN DE ESTADOS:', 'color: #333; font-weight: bold');
  const distribution = debugDetails.reduce((acc, detail) => {
    const status = detail['Estado'];
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  
  const distributionFormatted = Object.entries(distribution).map(([status, count]) => ({
    Estado: status,
    Cantidad: count,
    Porcentaje: `${((count / hpia.length) * 100).toFixed(1)}%`
  }));
  console.table(distributionFormatted);

  // Elementos críticos o que requieren acción
  const criticalOrWarn = debugDetails.filter(d => 
    d['Estado'].includes('CRÍTICO') || d['Estado'].includes('MALO')
  );
  if (criticalOrWarn.length > 0) {
    console.error(
      `%c🚨 REQUIEREN ACCIÓN INMEDIATA (${criticalOrWarn.length}):`,
      'color: #ff0000; font-weight: bold; font-size: 13px'
    );
    console.table(criticalOrWarn);
  }

  console.groupEnd();
}

/**
 * Retorna etiqueta completa descriptiva para un valor hpov
 * 
 * @param {number|null} hpov 
 * @returns {string}
 */
function getStatusLabel(hpov) {
  const labels = {
    null: '⚪ SIN DATOS',
    1: '🔴 CRÍTICO - Hipoxemia severa',
    2: '🟠 MALO - Hipoxemia moderada',
    3: '🟡 TOLERABLE - Ligeramente bajo',
    4: '🟢 ACEPTABLE - Dentro de rango',
    5: '🟢 BUENO - Margen seguro',
    6: '🔵 EXCELENTE - Óptimo'
  };
  return labels[hpov] || 'DESCONOCIDO';
}

/**
 * Retorna etiqueta corta para formato tabla
 * 
 * @param {number|null} hpov 
 * @returns {string}
 */
function getStatusLabelShort(hpov) {
  const labels = {
    null: '⚪ SIN DATOS',
    1: '🔴 CRÍTICO',
    2: '🟠 MALO',
    3: '🟡 TOLERABLE',
    4: '🟢 ACEPTABLE',
    5: '🟢 BUENO',
    6: '🔵 EXCELENTE'
  };
  return labels[hpov] || '⚪ DESCONOCIDO';
}

/**
 * Indica la acción clínica requerida según el estado
 * 
 * @param {number|null} hpov 
 * @returns {string}
 */
function getActionRequired(hpov) {
  const actions = {
    null: 'Revisar datos',
    1: '⚠️ INYECTAR O2 URGENTE',
    2: '⚠️ INYECTAR O2',
    3: 'Monitorear',
    4: 'Monitorear',
    5: 'Normal',
    6: 'Vigilar exceso'
  };
  return actions[hpov] || 'N/A';
}

/**
 * Realiza validaciones sobre un elemento y retorna objeto con resultados
 * 
 * @param {Object} element 
 * @returns {Object} Objeto con resultados de validaciones
 */
function performValidations(element) {
  const { cntrl, upval, measu, loval } = element;
  
  return {
    'cntrl===true': cntrl === true ? '✓' : '✗',
    'measu!=null': measu !== null ? '✓' : '✗',
    'upval/loval!=null': (upval !== null || loval !== null) ? '✓' : '✗',
    'upval>=loval': (upval === null || loval === null || upval >= loval) ? '✓' : '✗'
  };
}

